import { storageAPI } from '@/utils/api'

export default {
  namespaced: true,
  
  state: {
    config: null,
    status: null,
    availableMethods: [],
    loading: false,
    error: null,
    isInitialized: false
  },
  
  getters: {
    // Get current storage method
    currentMethod: (state) => state.config?.Method || 'local',
    
    // Check if Firebase is enabled
    isFirebaseEnabled: (state) => state.config?.Method === 'firebase',
    
    // Check if Local storage is enabled
    isLocalStorageEnabled: (state) => state.config?.Method === 'local',
    
    // Get storage configuration
    getConfig: (state) => state.config,
    
    // Get storage status
    getStatus: (state) => state.status,
    
    // Get available methods
    getAvailableMethods: (state) => state.availableMethods,
    
    // Check if storage is available
    isStorageAvailable: (state) => state.status?.IsAvailable || false,
    
    // Get max file size
    maxFileSize: (state) => state.config?.MaxFileSize || 10 * 1024 * 1024,
    
    // Get allowed file types
    allowedFileTypes: (state) => state.config?.AllowedFileTypes || [],
    
    // Get file extensions mapping
    fileExtensions: (state) => state.config?.FileExtensions || {}
  },
  
  mutations: {
    SET_CONFIG(state, config) {
      state.config = config
    },
    
    SET_STATUS(state, status) {
      state.status = status
    },
    
    SET_AVAILABLE_METHODS(state, methods) {
      state.availableMethods = methods
    },
    
    SET_LOADING(state, loading) {
      state.loading = loading
    },
    
    SET_ERROR(state, error) {
      state.error = error
    },
    
    SET_INITIALIZED(state, initialized) {
      state.isInitialized = initialized
    },
    
    CLEAR_ERROR(state) {
      state.error = null
    }
  },
  
  actions: {
    /**
     * Initialize storage configuration
     */
    async initialize({ commit, dispatch }) {
      try {
        commit('SET_LOADING', true)
        commit('CLEAR_ERROR')
        
        // Fetch configuration
        await dispatch('fetchConfig')
        
        // Fetch status
        await dispatch('fetchStatus')
        
        // Fetch available methods
        await dispatch('fetchAvailableMethods')
        
        commit('SET_INITIALIZED', true)
      } catch (error) {
        console.error('Failed to initialize storage:', error)
        commit('SET_ERROR', error.message)
      } finally {
        commit('SET_LOADING', false)
      }
    },
    
    /**
     * Fetch storage configuration from backend
     */
    async fetchConfig({ commit }) {
      try {
        const response = await storageAPI.getStorageConfig()
        
        if (response.data.success) {
          commit('SET_CONFIG', response.data.config)
          console.log('Storage config loaded:', response.data.config.Method)
        } else {
          throw new Error('Failed to fetch storage configuration')
        }
      } catch (error) {
        console.error('Error fetching storage config:', error)
        commit('SET_ERROR', error.message)
        throw error
      }
    },
    
    /**
     * Fetch storage status from backend
     */
    async fetchStatus({ commit }) {
      try {
        const response = await storageAPI.getStorageStatus()
        
        if (response.data.success) {
          commit('SET_STATUS', response.data.status)
        } else {
          throw new Error('Failed to fetch storage status')
        }
      } catch (error) {
        console.error('Error fetching storage status:', error)
        commit('SET_ERROR', error.message)
        throw error
      }
    },
    
    /**
     * Fetch available storage methods from backend
     */
    async fetchAvailableMethods({ commit }) {
      try {
        const response = await storageAPI.getAvailableMethods()
        
        if (response.data.success) {
          commit('SET_AVAILABLE_METHODS', response.data.methods)
        } else {
          throw new Error('Failed to fetch available methods')
        }
      } catch (error) {
        console.error('Error fetching available methods:', error)
        commit('SET_ERROR', error.message)
        throw error
      }
    },
    
    /**
     * Refresh storage configuration
     */
    async refreshConfig({ commit, dispatch }) {
      commit('SET_INITIALIZED', false)
      await dispatch('initialize')
    },
    
    /**
     * Clear storage error
     */
    clearError({ commit }) {
      commit('CLEAR_ERROR')
    }
  }
} 